
import { defineComponent, onMounted, ref } from 'vue'
import { message } from 'ant-design-vue'
import {
  getQualityThresholds,
  updateQualityThresholds,
  QualityThresholdVersion,
} from '@/services/admin'

type NumericFieldKey = 'min_text_coverage' | 'max_font_size_error_pt' | 'max_text_center_error_ratio' | 'max_text_size_error_ratio' | 'max_visual_diff_ratio' | 'max_visual_mean_diff'

export default defineComponent({
  name: 'QualitySettings',
  setup() {
    const loading = ref(false)
    const saving = ref(false)
    const current = ref<QualityThresholdVersion | null>(null)
    const history = ref<QualityThresholdVersion[]>([])
    const requireContentOcr = ref(true)
    const note = ref('')
    const form = ref<Record<NumericFieldKey, string>>({
      min_text_coverage: '0.9',
      max_font_size_error_pt: '2',
      max_text_center_error_ratio: '0.035',
      max_text_size_error_ratio: '0.12',
      max_visual_diff_ratio: '0.12',
      max_visual_mean_diff: '0.08',
    })
    const fields: Array<{ key: NumericFieldKey; label: string; hint: string; type: string; step: string }> = [
      { key: 'min_text_coverage', label: '文本覆盖率', hint: '0-1，越高越严格', type: 'number', step: '0.01' },
      { key: 'max_font_size_error_pt', label: '字号误差 pt', hint: 'OCR 推导字号允许偏差', type: 'number', step: '0.5' },
      { key: 'max_text_center_error_ratio', label: '位置误差比例', hint: '文本框中心点误差', type: 'number', step: '0.005' },
      { key: 'max_text_size_error_ratio', label: '尺寸误差比例', hint: '文本框宽高误差', type: 'number', step: '0.01' },
      { key: 'max_visual_diff_ratio', label: 'Visual diff 比例', hint: '预览像素差异比例', type: 'number', step: '0.01' },
      { key: 'max_visual_mean_diff', label: 'Visual mean diff', hint: '平均像素差异强度', type: 'number', step: '0.01' },
    ]

    const syncForm = (item: QualityThresholdVersion) => {
      current.value = item
      const thresholds = item.thresholds
      fields.forEach((field) => {
        form.value[field.key] = String(thresholds[field.key] ?? form.value[field.key])
      })
      requireContentOcr.value = thresholds.require_content_ocr !== false
    }

    const loadThresholds = async () => {
      loading.value = true
      try {
        const result = await getQualityThresholds()
        history.value = result.history
        syncForm(result.current)
      }
      catch {
        message.error('加载质量阈值失败')
      }
      finally {
        loading.value = false
      }
    }

    const saveThresholds = async () => {
      saving.value = true
      try {
        const thresholds: Record<string, number | string | boolean> = {
          profile: 'admin-configured',
          require_content_ocr: requireContentOcr.value,
        }
        fields.forEach((field) => {
          thresholds[field.key] = Number(form.value[field.key])
        })
        const created = await updateQualityThresholds({ thresholds, note: note.value })
        message.success(`已创建质量阈值 v${created.version}`)
        note.value = ''
        await loadThresholds()
      }
      catch (error) {
        message.error(error instanceof Error ? error.message : '保存质量阈值失败')
      }
      finally {
        saving.value = false
      }
    }

    const formatDateTime = (value?: string | null) => {
      if (!value) return '-'
      const date = new Date(value.replace(' ', 'T'))
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleString('zh-CN', { hour12: false })
    }

    onMounted(loadThresholds)

    return {
      loading,
      saving,
      current,
      history,
      requireContentOcr,
      note,
      form,
      fields,
      saveThresholds,
      formatDateTime,
    }
  },
})
